import { useState, useEffect } from "react";
import { ShoppingCart, Star, ArrowRight, Truck, RefreshCcw } from "lucide-react";
import StatusModal from "../components/StatusModal";
import PaystackPop from "@paystack/inline-js";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import authorImage from "../assets/AMARAOYEKA_7crpd.JPG.jpeg";
import bookCover from "../assets/wait work cover.PNG";
import API_BASE_URL from "../config";

export default function BookShowcase() {
  const [quantity, setQuantity] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("NG");
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  // 1. Fetch Countries on Mount
  useEffect(() => {
    const fetchCountries = async () => {
      setIsLoadingCountries(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/countries`);
        const data = await res.json();
        if (data.data) {
          setCountries(data.data);
        }
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setIsLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  // 2. Fetch States when Country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (!country) return;
      setIsLoadingStates(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/states/${country}`);
        const data = await res.json();
        if (data.data) {
          setStates(data.data);
        } else {
          setStates([]);
        }
      } catch (err) {
        console.error("Error fetching states:", err);
        setStates([]);
      } finally {
        setIsLoadingStates(false);
      }
    };
    fetchStates();
    setState("");
    setCity("");
  }, [country]);

  // 3. Fetch Cities when State changes
  useEffect(() => {
    const fetchCities = async () => {
      if (!country || !state) {
        setCities([]);
        return;
      }
      setIsLoadingCities(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/get-cities?country_code=${country}&state=${state}`);
        const data = await res.json();
        if (data.success) {
          setCities(data.data || []);
        } else {
          setCities([]);
        }
      } catch (err) {
        console.error("Error fetching cities:", err);
        setCities([]);
      } finally {
        setIsLoadingCities(false);
      }
    };
    fetchCities();
    setCity("");
  }, [state, country]);

  const [shippingRates, setShippingRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [shipmentId, setShipmentId] = useState(null);
  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [bookType, setBookType] = useState("physical"); // physical or digital
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });

  const showStatus = (type, title, message) => {
    setStatusModal({ isOpen: true, type, title, message });
  };

  // Animation trigger
  useEffect(() => {
    setIsVisible(true);
  }, []);
  const currentDate = new Date();
  const preOrderStartDate = new Date("2026-04-01T00:00:00+01:00");
  const preOrderEndDate = new Date("2026-06-01T00:00:00+01:00");

  const isPreOrder = currentDate >= preOrderStartDate && currentDate < preOrderEndDate;
  const isBeforePreOrder = currentDate < preOrderStartDate;

  const getPrice = () => {
    if (isPreOrder || isBeforePreOrder) {
      return bookType === "digital" ? 10000 : 15000;
    } else {
      return bookType === "digital" ? 12500 : 20000;
    }
  };

  const currentPrice = getPrice();
  let isButtonDisabled = false;
  let buttonText = "Order Now";
  let bookStatus = "Available";

  if (isBeforePreOrder) {
    isButtonDisabled = true;
    buttonText = "Pre-order Starts May 1st";
    bookStatus = "Coming Soon";
  } else if (isPreOrder) {
    isButtonDisabled = false;
    buttonText = "Pre-order Now";
    bookStatus = "Pre-order";
  } else {
    isButtonDisabled = false;
    buttonText = "Order Now";
    bookStatus = "Available";
  }

  // Book data
  const book = {
    title: "The Wait Work: Embrace the Time Gap, Create Your Triumph",
    author: "Oyeka Amara Kate",
    description: "You’re not stuck; you’re being shaped. What if the waiting season you despise is the very ground where your strength, wisdom, and clarity are being forged? In The Wait Work, you’ll see that every delay has meaning. This book uncovers the hidden power behind life’s pauses, showing you why things seem slow and how to turn the inbetween moments into your training ground.",
    status: bookStatus,
    price: currentPrice,
    releaseDate: "1st June 2026",
    tags: ["Inspirational", "Personal Development", "Life Lessons", "Motivation"]
  };



  const fetchShippingRates = async () => {
    if (!address || !city || !state || !country) {
      showStatus("error", "Missing Information", "Please fill in your address, city, state, and country to get shipping rates.");
      return;
    }

    setIsCalculatingShipping(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/get-shipping-rates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address,
          city,
          state,
          country,
          zip,
          firstName: name.split(" ")[0],
          lastName: name.split(" ")[1] || "",
          email,
          phone: phone,
          quantity
        }),
      });

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const errorText = await res.text();
        console.error("Non-JSON response from server:", errorText.substring(0, 200));
        throw new Error(`Server returned an invalid response (Status: ${res.status}). This might be due to a timeout. Please try again.`);
      }

      const data = await res.json();
      if (data.success) {
        setShippingRates(data.rates);
        setShipmentId(data.shipment_id);
        if (data.rates.length > 0) {
          setSelectedRate(data.rates[0]); // Select first rate by default
        }
      } else {
        throw new Error(data.details || "Failed to fetch rates");
      }
    } catch (err) {
      console.error("Shipping error:", err);
      showStatus("error", "Shipping Error", `Could not fetch shipping rates: ${err.message}`);
    } finally {
      setIsCalculatingShipping(false);
    }
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setCity("");
    setState("");
    setZip("");
    setCountry("NG");
    setQuantity(1);
    setShippingRates([]);
    setSelectedRate(null);
    setShipmentId(null);
    setBookType("physical");
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();

    if (bookType === 'physical' && !selectedRate) {
      showStatus("error", "Shipping Required", "Please select a shipping method.");
      return;
    }

    setIsProcessing(true);

    try {
      const deliveryFee = bookType === "physical" ? selectedRate.amount : 0;
      const totalAmount = book.price * quantity + deliveryFee;
      const amountInKobo = Math.round(totalAmount * 100);

      const initRes = await fetch(`${API_BASE_URL}/api/initiate-preorder-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          amount: amountInKobo,
          name: bookType === 'digital' ? 'Digital Customer' : name,
          phone: bookType === 'digital' ? 'N/A' : phone,
          address: bookType === 'digital' ? 'Digital Delivery' : `${address}, ${city}, ${state}, ${country}`,
          quantity,
          bookTitle: book.title,
          bookType,
          deliveryRegion: bookType === 'physical' ? (country === 'NG' ? 'nigeria' : 'international') : 'n/a',
          shipmentId: bookType === 'physical' ? shipmentId : null,
          rateId: bookType === 'physical' ? selectedRate.rate_id : null,
          deliveryFee: deliveryFee,
          itemPrice: book.price
        }),
      });

      const initData = await initRes.json();

      if (!initData.access_code) {
        throw new Error("Payment access code not received.");
      }

      // 2. Launch Paystack modal using only access code
      const paystack = new PaystackPop();
      paystack.newTransaction({
        accessCode: initData.access_code,

        onSuccess: async (transaction) => {
          try {
            // 3. Verify payment on backend
            console.log("Starting payment verification for reference:", transaction.reference);
            const verifyRes = await fetch(`${API_BASE_URL}/api/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ reference: transaction.reference }),
            });

            console.log("Verification response received:", verifyRes.status);
            const verifyData = await verifyRes.json();
            console.log("Verification data:", verifyData);

            if (verifyData.success) {
              showStatus(
                "success",
                "Order Successful!",
                bookType === 'digital'
                  ? "Your digital copy has been sent to your email. Please check your inbox (and spam folder)."
                  : "Your order has been placed successfully. Our fulfillment team will contact you soon."
              );
              setShowModal(false);
              resetForm();
            }
          } catch (err) {
            console.error("Post-payment error details:", err);
            showStatus(
              "error",
              "Something went wrong",
              `We couldn't process your order: ${err.message}. Please contact support if your payment was deducted.`
            );
          } finally {
            setIsProcessing(false);
          }
        },

        onCancel: () => {
          showStatus("error", "Payment Cancelled", "The payment process was cancelled.");
          setIsProcessing(false);
        },
      });
    } catch (err) {
      console.error("Init error:", err);
      showStatus("error", "Initiation Error", "There was an error initiating the payment process. Please try again.");
      setIsProcessing(false);
    }
  };



  return (
    <div className="min-h-screen bg-gray-50">

      {/* Hero Section with Golden Yellow Accents */}
      <div className="relative bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="max-w-6xl mx-auto px-4 pt-40 pb-20 md:pt-44 md:pb-24 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className={`transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4">You’re Not Stuck; You’re Being Shaped.</h1>
              <p className="text-xl mb-6 text-yellow-200">Discover the hidden power behind life’s pauses and learn how to turn every delay into your ground for strength, wisdom, and clarity.</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {book.tags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-yellow-600 bg-opacity-80 rounded-full text-sm font-medium">
                    {tag}
                  </span>
                ))}
              </div>
              <a href="#book-details" className="inline-flex items-center justify-center w-full sm:w-auto px-6 py-3 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-500 transition-colors">
                Learn More <ArrowRight size={16} className="ml-2" />
              </a>
            </div>

            <div className={`flex justify-center transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <div className="w-56 sm:w-64 md:w-80 aspect-[2/3] md:h-[28rem] rounded-lg shadow-2xl transform rotate-3 relative overflow-hidden group hover:rotate-0 transition-transform duration-500">
                <img
                  src={bookCover}
                  alt={book.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Book Details Section */}
      <div id="book-details" className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Book Information */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-800">{book.title}</h2>
                <p className="text-gray-600 text-lg">{book.author}</p>
                <div className="flex items-center mt-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-500 fill-current" />
                  ))}
                  <span className="ml-2 text-sm text-gray-600">
                    {currentDate < preOrderEndDate ? 'Pre-release anticipation' : 'Highly Recommended'}
                  </span>
                </div>
              </div>
              <span className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                {book.status}
              </span>
            </div>

            <div className="border-t border-b border-gray-200 py-6 my-6">
              <h3 className="text-lg font-semibold mb-4 text-yellow-600">About the Book</h3>
              <p className="text-gray-700 leading-relaxed">{book.description}</p>
              <p className="text-gray-700 leading-relaxed mt-4">
                From missed opportunities and unready titles to the people and places still being prepared for you, every chapter reveals why the wait is necessary and how to walk through it with purpose. Waiting is not wasted time. It is the work that prepares you for more to come.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-600">Price</p>
                <p className="text-3xl font-bold text-yellow-600">₦{book.price.toLocaleString()}</p>
              </div>
              {currentDate < preOrderEndDate && (
                <div className="sm:text-right">
                  <p className="text-sm text-gray-600">Expected Release</p>
                  <p className="text-gray-800 font-medium">{book.releaseDate}</p>
                </div>
              )}
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">{currentDate < preOrderEndDate ? "Pre-order" : "Order"} Quantity:</p>
              <div className="flex items-center space-x-4">
                {/* <button
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                >
                  <span className="text-lg font-bold">-</span>
                </button>
                <span className="text-lg font-semibold w-10 text-center">{quantity}</span> */}
                <button
                  className={`w-full flex items-center justify-center space-x-2 px-6 py-4 rounded-lg transition-colors ${isButtonDisabled
                    ? "bg-gray-400 cursor-not-allowed text-white"
                    : "bg-yellow-600 hover:bg-yellow-500 text-white"
                    }`}
                  onClick={() => !isButtonDisabled && setShowModal(true)}
                  disabled={isButtonDisabled}
                >
                  <ShoppingCart size={20} />
                  <span>{buttonText}</span>
                </button>
                {showModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                      <h2 className="text-xl font-bold mb-4">{currentDate < preOrderEndDate ? "Pre-order" : "Order"} Details</h2>

                      <div className="mb-6">
                        <p className="text-sm font-medium text-gray-700 mb-2">Select Copy Type</p>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setBookType("physical")}
                            className={`py-2 px-4 rounded-lg border-2 transition-all ${bookType === 'physical' ? 'border-yellow-600 bg-yellow-50 text-yellow-700' : 'border-gray-200 text-gray-500'}`}
                          >
                            Physical Copy
                          </button>
                          <button
                            type="button"
                            onClick={() => setBookType("digital")}
                            className={`py-2 px-4 rounded-lg border-2 transition-all ${bookType === 'digital' ? 'border-yellow-600 bg-yellow-50 text-yellow-700' : 'border-gray-200 text-gray-500'}`}
                          >
                            Digital Copy
                          </button>
                        </div>
                      </div>

                      <form onSubmit={handleModalSubmit} className="space-y-4">
                        {bookType === 'physical' && (
                          <div className="space-y-4">
                            <p className="text-sm font-medium text-gray-700">Customer & Delivery Information</p>
                            <div className="space-y-4">
                              <div className="space-y-1">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Full Name</label>
                                <input
                                  type="text"
                                  placeholder="Full Name"
                                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none transition-all"
                                  value={name}
                                  onChange={(e) => setName(e.target.value)}
                                  required
                                />
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Email Address</label>
                                  <input
                                    type="email"
                                    placeholder="email@example.com"
                                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none transition-all"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Phone Number</label>
                                  <PhoneInput
                                    country={'ng'}
                                    value={phone}
                                    onChange={phone => setPhone(phone)}
                                    inputStyle={{ width: '100%', height: '48px', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                    buttonStyle={{ borderRadius: '8px 0 0 8px', border: '1px solid #e5e7eb' }}
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Country</label>
                                  <select
                                    className="w-full px-4 py-3 border rounded-lg bg-white focus:ring-2 focus:ring-yellow-500 outline-none transition-all"
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}
                                    required={bookType === 'physical'}
                                    disabled={isLoadingCountries}
                                  >
                                    <option value="">{isLoadingCountries ? "Loading..." : "Select Country"}</option>
                                    {countries.map((c, i) => (
                                      <option key={i} value={c.isoCode || c.name || c}>
                                        {c.name || c}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">ZIP / Postal Code</label>
                                  <input
                                    type="text"
                                    placeholder="100001"
                                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none transition-all"
                                    value={zip}
                                    onChange={(e) => setZip(e.target.value)}
                                  />
                                </div>
                              </div>

                              <div className="space-y-1">
                                <div className="flex justify-between items-center mt-1">
                                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Street Address</label>
                                  <span className={`text-[10px] font-bold ${address.length >= 45 ? 'text-red-500' : 'text-gray-400'}`}>
                                    {address.length}/45
                                  </span>
                                </div>
                                <textarea
                                  placeholder="House Number, Street Name, etc."
                                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none transition-all"
                                  value={address}
                                  onChange={(e) => setAddress(e.target.value)}
                                  required={bookType === 'physical'}
                                  maxLength={45}
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                                    State / Province
                                  </label>
                                  <select
                                    className="w-full px-4 py-3 border rounded-lg bg-white focus:ring-2 focus:ring-yellow-500 outline-none transition-all"
                                    value={state}
                                    onChange={(e) => setState(e.target.value)}
                                    required={bookType === 'physical'}
                                    disabled={isLoadingStates || !country}
                                  >
                                    <option value="">{isLoadingStates ? "Loading states..." : "Select State"}</option>
                                    {states.map((s, i) => (
                                      <option key={i} value={s.isoCode || s.name || s}>
                                        {s.name || s}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className="space-y-1">
                                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">City</label>
                                  {cities.length > 0 || isLoadingCities ? (
                                    <select
                                      className="w-full px-4 py-3 border rounded-lg bg-white focus:ring-2 focus:ring-yellow-500 outline-none transition-all"
                                      value={city}
                                      onChange={(e) => setCity(e.target.value)}
                                      required={bookType === 'physical'}
                                      disabled={isLoadingCities || !state}
                                    >
                                      <option value="">{isLoadingCities ? "Loading cities..." : "Select City"}</option>
                                      {cities.map((c, i) => {
                                        const cityName = typeof c === 'string' ? c : (c.name || c.city);
                                        return (
                                          <option key={i} value={cityName}>
                                            {cityName}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  ) : (
                                    <input
                                      type="text"
                                      placeholder="City"
                                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none transition-all"
                                      value={city}
                                      onChange={(e) => setCity(e.target.value)}
                                      required={bookType === 'physical'}
                                      disabled={!state}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={fetchShippingRates}
                              disabled={isCalculatingShipping || !address || !city || !state || !name || !email || !phone}
                              className={`w-full py-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all border ${isCalculatingShipping || !address || !city || !state || !name || !email || !phone
                                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                : "bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100"
                                }`}
                            >
                              {isCalculatingShipping ? (
                                <RefreshCcw size={16} className="animate-spin" />
                              ) : (
                                <Truck size={16} />
                              )}
                              {shippingRates.length > 0 ? "Update Shipping Rates" : "Calculate Shipping"}
                            </button>

                            {shippingRates.length > 0 && (
                              <div className="space-y-2 mt-4 animate-in fade-in slide-in-from-top-2">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Select Shipping Method</p>
                                {shippingRates.map((rate) => (
                                  <label
                                    key={rate.rate_id}
                                    className={`flex items-center justify-between p-3 border-2 rounded-xl cursor-pointer transition-all ${selectedRate?.rate_id === rate.rate_id
                                      ? 'border-yellow-600 bg-yellow-50 shadow-sm'
                                      : 'border-gray-100 hover:border-gray-200'
                                      }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <input
                                        type="radio"
                                        name="shippingRate"
                                        className="accent-yellow-600"
                                        checked={selectedRate?.rate_id === rate.rate_id}
                                        onChange={() => setSelectedRate(rate)}
                                      />
                                      <div className="flex items-center gap-3">
                                        {rate.carrier_logo ? (
                                          <img src={rate.carrier_logo} alt={rate.courier_name} className="w-8 h-8 object-contain rounded bg-white p-1 border border-gray-100" />
                                        ) : (
                                          <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded">
                                            <Truck size={14} className="text-gray-400" />
                                          </div>
                                        )}
                                        <div>
                                          <p className="text-sm font-bold text-gray-800">{rate.courier_name || rate.carrier_name}</p>
                                          <p className="text-xs text-gray-500">{rate.service_type || 'Standard Delivery'}</p>
                                        </div>
                                      </div>
                                    </div>
                                    <p className="text-sm font-bold text-yellow-700">₦{rate.amount.toLocaleString()}</p>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {bookType === 'digital' && (
                          <input
                            type="email"
                            placeholder="Email Address"
                            className="w-full px-4 py-3 border rounded-lg"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                        )}

                        <div className="flex justify-between items-center py-2 border-t">
                          <p className="font-medium">Quantity:</p>
                          <div className="flex space-x-3 items-center">
                            <button
                              type="button"
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200"
                              onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            >
                              -
                            </button>
                            <span className="font-bold">{quantity}</span>
                            <button
                              type="button"
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200"
                              onClick={() => setQuantity(quantity + 1)}
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Book Price ({quantity}x)</span>
                            <span>₦{(book.price * quantity).toLocaleString()}</span>
                          </div>
                          {bookType === 'physical' && selectedRate && (
                            <div className="flex justify-between">
                              <span>Delivery Fee ({selectedRate.courier_name})</span>
                              <span>₦{selectedRate.amount.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold text-lg border-t pt-2 text-yellow-700">
                            <span>Total</span>
                            <span>₦{(book.price * quantity + (bookType === 'physical' && selectedRate ? selectedRate.amount : 0)).toLocaleString()}</span>
                          </div>
                        </div>

                        {bookType === 'digital' && (
                          <p className="text-xs text-gray-500 italic">
                            * The digital copy will be sent securely to your email address or available for download after payment verification.
                          </p>
                        )}

                        <button
                          type="submit"
                          className={`w-full py-4 rounded-lg transition-all font-semibold shadow-md ${isProcessing || (bookType === 'physical' && !selectedRate)
                            ? "bg-gray-400 cursor-not-allowed text-white opacity-70"
                            : "bg-yellow-600 hover:bg-yellow-500 text-white"
                            }`}
                          disabled={isProcessing || (bookType === 'physical' && !selectedRate)}
                        >
                          {isProcessing ? "Processing..." : (bookType === 'physical' && !selectedRate ? "Select Shipping First" : "Pay Now")}
                        </button>
                      </form>

                      <button onClick={() => setShowModal(false)} className="text-sm text-red-500 mt-4">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>

            {/* <button className="w-full flex items-center justify-center space-x-2 bg-yellow-600 hover:bg-yellow-500 text-white px-6 py-4 rounded-lg transition-colors duration-300">
              <ShoppingCart size={20} />
              <span>Pre-order (Coming Soon)</span>
            </button> */}


          </div>

          {/* Author and Newsletter */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <h3 className="text-xl font-bold text-yellow-600 mb-6">About the Author</h3>
              <div className="flex flex-col items-center mb-6">
                <img
                  src={authorImage}
                  alt={book.author}
                  className="w-32 h-32 rounded-full object-cover shadow-lg border-4 border-yellow-500 mb-4"
                />
                <div>
                  <p className="text-xl font-bold text-gray-900 leading-tight">{book.author}</p>
                  <p className="text-yellow-600 font-medium italic">Author & Mentor</p>
                </div>
              </div>
              <div className="text-left border-t border-gray-100 pt-6">
                <p className="text-gray-700 leading-relaxed">
                  Oyeka Amara Kate is a seasoned mentor with years of experience guiding students, professionals, and executives. A sought-after speaker and career referee, she has impacted numerous lives through mentorship and talent management.
                </p>
                <p className="text-gray-700 leading-relaxed mt-4">
                  She contributes to ICAN and CITN as a subject matter expert and examiner. Passionate about social equity and justice, Amara is committed to empowering others.
                </p>
              </div>
            </div>


          </div>
        </div>
      </div>

      {/* Testimonials/Endorsements Section */}
      {/* <div className="bg-yellow-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Early Praise for "The Wait Work"</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "A refreshing new voice in Nigerian literature that speaks to the soul of patience in our hurried world.",
                author: "Chimamanda Adichie",
                title: "Author"
              },
              {
                quote: "Kate weaves a tapestry of human emotions with such delicate precision that you feel every moment of the wait.",
                author: "The Lagos Review",
                title: "Literary Magazine"
              },
              {
                quote: "A promising debut that signals the arrival of an important new voice in African literature.",
                author: "Wole Soyinka",
                title: "Nobel Laureate"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-yellow-700 p-6 rounded-xl">
                <p className="italic mb-4">"{testimonial.quote}"</p>
                <p className="font-medium">{testimonial.author}</p>
                <p className="text-yellow-200 text-sm">{testimonial.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div> */}

      {/* Status Modal for Success/Error messages */}
      <StatusModal
        {...statusModal}
        onClose={() => setStatusModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}

